<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  user_id: '',
  manager_id: '',
  is_manager: false,
  code: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  gender: '',
  blood_group: '',
  department_id: '',
  position_id: '',
  shift_id: '',
  employment_type: 'full_time',
  joining_date: '',
  confirmation_date: '',
  leaving_date: '',
  address: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relation: '',
});

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      form: DEFAULT_FORM(),
      errors: {},
      isEdit: false,
      employeeId: null,
      users: [],
      roles: [],
      managers: [],
      departments: [],
      positions: [],
      shifts: [],
      genderOptions: ['male', 'female', 'other'],
      typeOptions: [
        { value: 'full_time', label: 'Full-time' },
        { value: 'part_time', label: 'Part-time' },
        { value: 'contract', label: 'Contract' },
        { value: 'intern', label: 'Intern' },
      ],
      bloodGroupOptions: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
    };
  },
  computed: {
    managerOptions() {
      return this.managers.filter((m) => String(m.id) !== String(this.employeeId));
    },
  },
  mounted() {
    this.fetchDropdowns();
    const id = this.$route.params.id;
    if (id) {
      this.isEdit = true;
      this.employeeId = id;
      this.fetchEmployee(id);
    }
  },
  methods: {

    applyPincodeResult(result) {
      this.form.city = result.city || this.form.city;
      this.form.state = result.state || this.form.state;
      this.form.country = result.country || this.form.country;
    },

    async fetchDropdowns() {
      try {
        const [usersRes, deptRes, posRes, shiftRes, managerRes, rolesRes] = await Promise.all([
          axios.get('/users/all-users'),
          axios.get('/hrms/departments/dropdown'),
          axios.get('/hrms/positions/dropdown'),
          axios.get('/hrms/shifts/dropdown'),
          axios.get('/hrms/employees/manager'),
          axios.get('/roles'),
        ]);
        this.users = usersRes.data.data || [];
        this.departments = deptRes.data.data || [];
        this.positions = posRes.data.data || [];
        this.shifts = shiftRes.data.data || [];
        this.managers = managerRes.data.data || [];
        this.roles = rolesRes.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    async fetchEmployee(id) {
      this.setLoading(true);
      try {
        const res = await axios.get(`/hrms/employees/${id}`);
        const e = res.data.data;
        this.form = {
          user_id: e.user_id || '',
          manager_id: e.manager_id || '',
          is_manager: !!e.is_manager,
          code: e.code || '',
          first_name: e.first_name || '',
          last_name: e.last_name || '',
          email: e.email || '',
          phone: e.phone || '',
          date_of_birth: this.toInputDate(e.date_of_birth),
          gender: e.gender || '',
          blood_group: e.blood_group || '',
          department_id: e.department_id || '',
          position_id: e.position_id || '',
          shift_id: e.shift_id || '',
          employment_type: e.employment_type || 'full_time',
          joining_date: this.toInputDate(e.joining_date),
          confirmation_date: this.toInputDate(e.confirmation_date),
          leaving_date: this.toInputDate(e.leaving_date),
          address: e.address || '',
          city: e.city || '',
          state: e.state || '',
          country: e.country || '',
          pincode: e.pincode || '',
          emergency_contact_name: e.emergency_contact_name || '',
          emergency_contact_phone: e.emergency_contact_phone || '',
          emergency_contact_relation: e.emergency_contact_relation || '',
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    toInputDate(val) {
      if (!val) return '';
      return String(val).slice(0, 10);
    },

    userLabel(u) {
      return u.email ? `${u.name} (${u.email})` : u.name;
    },

    onUserSelect(userId) {
      const selected = this.users.find((u) => u.id === userId);
      if (!selected || !selected.name) return;
      const parts = selected.name.trim().split(/\s+/);
      this.form.first_name = parts[0] || '';
      this.form.last_name = parts.slice(1).join(' ') || '';
      if (!this.form.email && selected.email) {
        this.form.email = selected.email;
      }
    },

    buildPayload() {
      const f = this.form;
      const nullify = (v) => (v === '' ? null : v);
      return {
        user_id: f.user_id || null,
        manager_id: nullify(f.manager_id),
        is_manager: !!f.is_manager,
        code: f.code,
        first_name: f.first_name,
        last_name: nullify(f.last_name),
        email: f.email,
        phone: nullify(f.phone),
        date_of_birth: nullify(f.date_of_birth),
        gender: f.gender,
        blood_group: nullify(f.blood_group),
        department_id: nullify(f.department_id),
        position_id: nullify(f.position_id),
        shift_id: nullify(f.shift_id),
        employment_type: f.employment_type,
        joining_date: nullify(f.joining_date),
        confirmation_date: nullify(f.confirmation_date),
        leaving_date: nullify(f.leaving_date),
        address: nullify(f.address),
        city: nullify(f.city),
        state: nullify(f.state),
        country: nullify(f.country),
        pincode: nullify(f.pincode),
        emergency_contact_name: nullify(f.emergency_contact_name),
        emergency_contact_phone: nullify(f.emergency_contact_phone),
        emergency_contact_relation: nullify(f.emergency_contact_relation),
      };
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let res;
        if (this.isEdit) {
          res = await axios.put(`/hrms/employees/${this.employeeId}`, this.buildPayload());
        } else {
          res = await axios.post('/hrms/employees', this.buildPayload());
        }
        this.$swalToast(res.data.message || 'Saved successfully.');
        this.$router.push('/hrms/employees');
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
          this.$swalToast('Please fix the highlighted errors.', 'error');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    cancel() {
      this.$router.push('/hrms/employees');
    },

    err(field) {
      return this.errors[field] ? this.errors[field][0] : '';
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content pb-4">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <div class="d-flex align-items-center gap-2">
          <a href="javascript:void(0);" class="btn btn-icon btn-light" @click="cancel"><i class="ti ti-arrow-left"></i></a>
          <h4 class="page-title mb-0">{{ isEdit ? 'Edit Employee' : 'Add Employee' }}</h4>
        </div>
      </div>

      <form @submit.prevent="save">

        <!-- Basic / Account -->
        <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
          <div class="card-header bg-white border-bottom py-3 px-4">
            <h5 class="mb-0 fw-bold emp-sec-title"><i class="ti ti-user-circle me-2"></i>Basic Details</h5>
          </div>
          <div class="card-body p-4">
            <div class="row">
              <div class="col-md-4 mb-3">
              <label class="form-label d-flex align-items-center justify-content-between">
                <span>User Account <span class="text-danger">*</span></span>
                <a href="javascript:void(0);"
                  data-bs-toggle="modal" data-bs-target="#admin_modal"
                  class="text-primary small"
                  title="Creates a new login account (email, password, role) for this person. Use this if they don't already have a user account — you'll still need to fill in their employee details below separately."
                  @click="$refs.subadminModal.add();">
                  <i class="ti ti-circle-plus me-1"></i>Add User <i class="ti ti-info-octagon"></i>
                </a>
              </label>
                <vue-select
                  :options="users"
                  :get-option-label="userLabel"
                  :reduce="u => u.id"
                  v-model="form.user_id"
                  placeholder="Select user"
                  :appendToBody="true"
                  @update:modelValue="onUserSelect"
                />
                <div v-if="err('user_id')" class="text-danger small mt-1">{{ err('user_id') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Employee Code <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.code" placeholder="e.g. ALGO001" />
                <div v-if="err('code')" class="text-danger small mt-1">{{ err('code') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Employment Type <span class="text-danger">*</span></label>
                <vue-select
                  :options="typeOptions"
                  label="label"
                  :reduce="t => t.value"
                  v-model="form.employment_type"
                  placeholder="Select type"
                  :appendToBody="true"
                  :clearable="false"
                />
                <div v-if="err('employment_type')" class="text-danger small mt-1">{{ err('employment_type') }}</div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">First Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.first_name" />
                <div v-if="err('first_name')" class="text-danger small mt-1">{{ err('first_name') }}</div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Last Name</label>
                <input type="text" class="form-control" v-model="form.last_name" />
                <div v-if="err('last_name')" class="text-danger small mt-1">{{ err('last_name') }}</div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Email <span class="text-danger">*</span></label>
                <input type="email" class="form-control" v-model="form.email" />
                <div v-if="err('email')" class="text-danger small mt-1">{{ err('email') }}</div>
              </div>

              <div class="col-md-3 mb-3">
                <label class="form-label">Phone</label>
                <input type="text" class="form-control" v-model="form.phone" placeholder="10 digits" maxlength="10" />
                <div v-if="err('phone')" class="text-danger small mt-1">{{ err('phone') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Personal -->
        <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
          <div class="card-header bg-white border-bottom py-3 px-4">
            <h5 class="mb-0 fw-bold emp-sec-title"><i class="ti ti-id me-2"></i>Personal Details</h5>
          </div>
          <div class="card-body p-4">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Date of Birth</label>
                <input type="date" class="form-control" v-model="form.date_of_birth" />
                <div v-if="err('date_of_birth')" class="text-danger small mt-1">{{ err('date_of_birth') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Gender <span class="text-danger">*</span></label>
                <vue-select
                  :options="genderOptions"
                  :get-option-label="g => g.charAt(0).toUpperCase() + g.slice(1)"
                  v-model="form.gender"
                  placeholder="Select gender"
                  :appendToBody="true"
                />
                <div v-if="err('gender')" class="text-danger small mt-1">{{ err('gender') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Blood Group</label>
                <vue-select
                  :options="bloodGroupOptions"
                  v-model="form.blood_group"
                  placeholder="Select blood group"
                  :appendToBody="true"
                />
                <div v-if="err('blood_group')" class="text-danger small mt-1">{{ err('blood_group') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Job -->
        <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
          <div class="card-header bg-white border-bottom py-3 px-4">
            <h5 class="mb-0 fw-bold emp-sec-title"><i class="ti ti-briefcase me-2"></i>Job Details</h5>
          </div>
          <div class="card-body p-4">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Department</label>
                <select class="form-select" v-model="form.department_id">
                  <option value="">Select department</option>
                  <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
                <div v-if="err('department_id')" class="text-danger small mt-1">{{ err('department_id') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Position</label>
                <select class="form-select" v-model="form.position_id">
                  <option value="">Select position</option>
                  <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
                <div v-if="err('position_id')" class="text-danger small mt-1">{{ err('position_id') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Shift</label>
                <vue-select
                  :options="shifts"
                  label="name"
                  :reduce="s => s.id"
                  v-model="form.shift_id"
                  placeholder="Select shift"
                  :appendToBody="true"
                />
                <div v-if="err('shift_id')" class="text-danger small mt-1">{{ err('shift_id') }}</div>
              </div>

              <!-- Reporting Manager -->
              <div class="col-md-4 mb-3">
                <label class="form-label" data-bs-toggle="tooltip" title="The manager this employee reports to. This is different from 'Is Manager?', which indicates whether this employee manages a team themselves.">
                  Reporting Manager <i class="ti ti-info-octagon"></i>
                </label>
                <vue-select
                  :options="managerOptions"
                  label="full_name"
                  :reduce="m => m.id"
                  v-model="form.manager_id"
                  placeholder="Select manager"
                  :appendToBody="true"
                />
                <div v-if="err('manager_id')" class="text-danger small mt-1">{{ err('manager_id') }}</div>
              </div>

             <div class="col-md-4 mb-3">
              <label class="form-label d-block" data-bs-toggle="tooltip" title="Turn this on if the employee manages a team (i.e. other employees can select them as their Reporting Manager).">
                Is Manager? <i class="ti ti-info-octagon"></i>
              </label>
                <div class="form-check form-switch mt-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isManagerSwitch"
                    v-model="form.is_manager"
                    style="cursor:pointer; width:42px; height:22px;"
                  />
                  <label class="form-check-label ms-2" for="isManagerSwitch" style="font-size:12.5px; color:#475569; cursor:pointer;">
                    {{ form.is_manager ? 'Yes — can manage a team' : 'No' }}
                  </label>
                </div>
                <div v-if="err('is_manager')" class="text-danger small mt-1">{{ err('is_manager') }}</div>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label">Joining Date <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="form.joining_date" />
                <div v-if="err('joining_date')" class="text-danger small mt-1">{{ err('joining_date') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Confirmation Date</label>
                <input type="date" class="form-control" v-model="form.confirmation_date" />
                <div v-if="err('confirmation_date')" class="text-danger small mt-1">{{ err('confirmation_date') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Leaving Date</label>
                <input type="date" class="form-control" v-model="form.leaving_date" />
                <div v-if="err('leaving_date')" class="text-danger small mt-1">{{ err('leaving_date') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
          <div class="card-header bg-white border-bottom py-3 px-4">
            <h5 class="mb-0 fw-bold emp-sec-title"><i class="ti ti-map-pin me-2"></i>Address</h5>
          </div>
          <div class="card-body p-4">
            <div class="row">
              <div class="col-md-12 mb-3">
                <label class="form-label">Address</label>
                <input type="text" class="form-control" v-model="form.address" />
                <div v-if="err('address')" class="text-danger small mt-1">{{ err('address') }}</div>
              </div>
              <div class="col-md-3 mb-3">
                <pincode-input
                  v-model="form.pincode"
                  :error="err('pincode')"
                  @resolved="applyPincodeResult"
                />
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">City</label>
                <input type="text" class="form-control" v-model="form.city" disabled placeholder="Auto-filled from pincode" />
                <div v-if="err('city')" class="text-danger small mt-1">{{ err('city') }}</div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">State</label>
                <input type="text" class="form-control" v-model="form.state" disabled placeholder="Auto-filled from pincode" />
                <div v-if="err('state')" class="text-danger small mt-1">{{ err('state') }}</div>
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Country</label>
                <input type="text" class="form-control" v-model="form.country" disabled placeholder="Auto-filled from pincode" />
                <div v-if="err('country')" class="text-danger small mt-1">{{ err('country') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Emergency -->
        <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
          <div class="card-header bg-white border-bottom py-3 px-4">
            <h5 class="mb-0 fw-bold emp-sec-title"><i class="ti ti-urgent me-2"></i>Emergency Contact</h5>
          </div>
          <div class="card-body p-4">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Contact Name</label>
                <input type="text" class="form-control" v-model="form.emergency_contact_name" />
                <div v-if="err('emergency_contact_name')" class="text-danger small mt-1">{{ err('emergency_contact_name') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Contact Phone</label>
                <input type="text" class="form-control" v-model="form.emergency_contact_phone" placeholder="10 digits" maxlength="10" />
                <div v-if="err('emergency_contact_phone')" class="text-danger small mt-1">{{ err('emergency_contact_phone') }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Relation</label>
                <input type="text" class="form-control" v-model="form.emergency_contact_relation" placeholder="e.g. Father" />
                <div v-if="err('emergency_contact_relation')" class="text-danger small mt-1">{{ err('emergency_contact_relation') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-light" @click="cancel">Cancel</button>
          <button type="submit" class="btn btn-primary px-4">{{ isEdit ? 'Update Employee' : 'Save Employee' }}</button>
        </div>

      </form>
      <subadmin-modal :roles="roles" ref="subadminModal" @updated="onUserAdded"></subadmin-modal>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.emp-sec-title { font-size: 13.5px; color: #0f172a; }
.emp-sec-title i { color: #6366f1; }
</style>